import { useState } from 'react'

import { Box, Button, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useHasShiftManagement } from 'modules/shift_management/hooks/useHasShiftManagement'
import PlanningVersionForm from 'modules/time_planning/lib/forms/PlanningVersionForm'
import numberOfRestDaysOptions from 'modules/time_planning/lib/numberOfRestDaysOptions'
import { planningToolOptions } from 'modules/time_planning/lib/planningToolOptions'
import { trackAddNewPlanningVersionSubmit } from 'modules/time_planning/lib/trackEvents'
import { usePlanningVersions } from 'modules/time_planning/store/collections/planningVersions'
import { useSchedules } from 'modules/work_schedules/store/collections/schedules'

export interface Props {
  employeeId: number
  onClose: () => void
  planningTool?: string
  scheduleId?: string
  numberOfRestDays?: string | number
}

const CreateModal = ({
  employeeId,
  onClose,
  planningTool,
  scheduleId,
  numberOfRestDays,
}: Props) => {
  const planningVersions = usePlanningVersions()
  const schedules = useSchedules()
  const { loading, canUseShiftManagement } = useHasShiftManagement()
  const hasRestDays = hasFeature('shift_management_rest_day')
  const effectiveAt = dateToString(moment(), 'ui')

  const createForm = (
    employeeId: number,
    effectiveAt: string,
    planningTool?: string,
    scheduleId?: string,
    numberOfRestDays?: string | number
  ) => {
    return new PlanningVersionForm({
      employee_id: employeeId,
      effective_at: effectiveAt,
      planning_tool: planningTool,
      schedule_id: isEmpty(scheduleId) ? null : Number(scheduleId),
      number_of_rest_days_in_cents: isNumber(numberOfRestDays) ? numberOfRestDays : null,
    })
  }

  const [form, setForm] = useState(
    createForm(employeeId, effectiveAt, planningTool, scheduleId, numberOfRestDays)
  )
  const defaultPlanningVersion = planningVersions.defaultPlanningVersion(employeeId)
  const minDate = defaultPlanningVersion.effectiveAt
  const [open, toggle] = useState<boolean>(form.get('number_of_rest_days_in_cents').value)

  const scheduleOptions = schedules.unarchived.map((schedule) => ({
    label: schedule.attributes.name,
    value: schedule.attributes.id,
  }))

  useEffect(() => {
    setForm(createForm(employeeId, effectiveAt, planningTool, scheduleId, numberOfRestDays))
  }, [employeeId, effectiveAt, planningTool, scheduleId, numberOfRestDays])

  async function onSubmit() {
    const { success } = await asyncAction(() =>
      planningVersions.create({
        ...form.data(),
        effective_at: dateUIToBackend(form.get('effective_at').value),
        schedule_id: form.isWorkScheduleTool ? form.get('schedule_id').value : undefined,
      })
    )

    if (success) {
      onClose()

      const scheduleId = form.get('schedule_id').value
      const workScheduleName = scheduleOptions.find((option) => option.value === scheduleId)?.label
      trackAddNewPlanningVersionSubmit(
        form.get('planning_tool').value,
        form.get('effective_at').value,
        form.get('number_of_rest_days_in_cents').value || undefined,
        workScheduleName
      )
    }
  }

  const onChangeNumberOfRestDaysCheckbox = (value: boolean) => {
    if (!value) {
      form.get('number_of_rest_days_in_cents').set(null)
    }

    toggle(value)
  }

  return (
    <Modal
      onClose={onClose}
      onSubmit={() => onSubmit()}
      illustration="document-edit"
      title={i18n.t('time_planning.planning_versions.modal.create.title')}
      description={i18n.t('time_planning.planning_versions.modal.create.description')}
      footer={
        <Button disabled={!form.isValid} submit>
          {i18n.t('time_planning.planning_versions.modal.create.button')}
        </Button>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box gap="s24">
          <Select
            none={i18n.t('common.select_one')}
            field={form.get('planning_tool')}
            label={i18n.t('time_planning.planning_versions.forms.planning_tool')}
            options={planningToolOptions(canUseShiftManagement)}
          />
          {form.isWorkScheduleTool && (
            <Select
              none={i18n.t('common.select_one')}
              field={form.get('schedule_id')}
              label={i18n.t('time_planning.planning_versions.forms.schedule_id')}
              options={scheduleOptions}
            />
          )}
          {hasRestDays && form.isShiftManagementTool && (
            <>
              <TogglerCheckbox
                label={i18n.t(
                  'time_planning.planning_versions.forms.apply_number_of_rest_days_checkbox'
                )}
                value={open}
                onChange={onChangeNumberOfRestDaysCheckbox}
              />
              {open && (
                <Select
                  field={form.get('number_of_rest_days_in_cents')}
                  label={i18n.t('time_planning.planning_versions.forms.number_of_rest_days')}
                  options={numberOfRestDaysOptions()}
                />
              )}
            </>
          )}
          <DayPicker
            label={i18n.t('time_planning.planning_versions.forms.effective_at')}
            field={form.get('effective_at')}
            minDate={minDate}
            pin
          />
        </Box>
      )}
    </Modal>
  )
}

export default observer(CreateModal)
